import { forwardRef, Inject, Injectable } from '@angular/core';
import { EventTrackingActions } from '@campus/ab-test';
import { ContentInterface, DalState, EduContent, EduContentTypeEnum, Result, ResultInterface } from '@campus/dal';
import { Store } from '@ngrx/store';
import { StudentTaskWithContentInterface } from '../../../interfaces';
import { ContentActionInterface } from '../content-action.interface';
import {
  ActionsForTaskInstanceEduContentOptionsInterface,
  CONTENT_OPENER_TOKEN,
  ContentActionDictionary,
  ContentActionType,
  ContentOpenActionsServiceInterface,
  ContentOpenerInterface,
} from './content-open-actions.service.interface';
import { DefaultContentOpenerService } from './default-content-opener.service';

@Injectable({
  providedIn: 'root',
})
export class ContentOpenActionsService implements ContentOpenActionsServiceInterface {
  constructor(
    @Inject(forwardRef(() => CONTENT_OPENER_TOKEN))
    private contentOpener: ContentOpenerInterface,
    @Inject(DefaultContentOpenerService)
    private defaultContentOpener: ContentOpenerInterface,
    private store: Store<DalState>
  ) {}

  public contentActionDictionary: ContentActionDictionary = {
    openEduContentAsExercise: {
      type: 'openEduContentAsExercise',
      handlerMethod: 'openEduContentAsExercise',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open oefening zonder oplossingen',
      handler: this.getHandler('openEduContentAsExercise'),
      isPrimary: true,
    },
    openEduContentAsSolution: {
      type: 'openEduContentAsSolution',
      handlerMethod: 'openEduContentAsSolution',
      label: 'Oplossing',
      icon: 'show-solution',
      tooltip: 'Open oefening met oplossingen',
      handler: this.getHandler('openEduContentAsSolution'),
    },
    openEduContentAsPreview: {
      type: 'openEduContentAsPreview',
      handlerMethod: 'openEduContentAsSolution',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open oefening',
      handler: this.getHandler('openEduContentAsSolution'),
      isPrimary: true,
    },
    openEduContentAsStream: {
      type: 'openEduContentAsStream',
      handlerMethod: 'openEduContentAsStream',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open het lesmateriaal',
      handler: this.getHandler('openEduContentAsStream'),
      isPrimary: true,
    },
    openEduContentAsDownload: {
      type: 'openEduContentAsDownload',
      handlerMethod: 'openEduContentAsDownload',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open het lesmateriaal',
      handler: this.getHandler('openEduContentAsDownload'),
      isPrimary: true,
    },
    openEduContentAsWebApp: {
      type: 'openEduContentAsWebApp',
      handlerMethod: 'openEduContentAsLink',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de web-app',
      handler: this.getHandler('openEduContentAsLink'),
      isPrimary: true,
    },
    openInstructions: {
      type: 'openInstructions',
      handlerMethod: 'openInstructions',
      label: 'Uitleg bekijken',
      icon: 'open-in-new',
      tooltip: 'Open de uitleg',
      handler: this.getHandler('openInstructions'),
    },
    openEduContentAsLink: {
      type: 'openEduContentAsLink',
      handlerMethod: 'openEduContentAsLink',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de link',
      handler: this.getHandler('openEduContentAsLink'),
      isPrimary: true,
    },
    openBoeke: {
      type: 'openBoeke',
      handlerMethod: 'openBoeke',
      label: 'Openen',
      icon: 'book',
      tooltip: 'Open het bordboek',
      handler: this.getHandler('openBoeke'),
      isPrimary: true,
    },
    openPaperExercise: {
      type: 'openPaperExercise',
      handlerMethod: 'openPaperExercise',
      label: 'Openen',
      icon: 'preview',
      tooltip: 'Open de oefening',
      handler: this.getHandler('openPaperExercise'),
      isPrimary: true,
    },
    openPaperExerciseAsSolution: {
      label: 'Oplossing',
      icon: 'show-solution',
      tooltip: 'Open oefening met oplossingen',
      handler: this.getHandler('openPaperExerciseAsSolution'),
    },
    openEduContentForReview: {
      type: 'openEduContentForReview',
      handlerMethod: 'openEduContentForReview',
      label: 'Bekijk',
      icon: 'preview',
      tooltip: 'Open oefening met antwoorden',
      handler: this.getHandler('openEduContentForReview'),
    },
    openEduContentForGrading: {
      type: 'openEduContentForGrading',
      handlerMethod: 'openEduContentForGrading',
      label: 'Bekijk en beoordeel',
      icon: 'manual-grading',
      tooltip: 'Bekijk en beoordeel de oefening',
      handler: this.getHandler('openEduContentForGrading'),
    },
    openEduContentAsHtml: {
      type: 'openEduContentAsHtml',
      handlerMethod: 'openEduContentAsHtml',
      label: 'Lezen',
      icon: 'preview',
      tooltip: 'Lees de tekst',
      handler: this.getHandler('openEduContentAsHtml'),
      isPrimary: true,
    },
    openLink: {
      type: 'openLink',
      handlerMethod: 'openLink',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de link',
      handler: this.getHandler('openLink'),
      isPrimary: true,
    },
  };

  /**
   * Gets all actions for the provided eduContent
   *
   * @param {ContentInterface} content
   * @returns {ContentActionInterface[]}
   * @memberof ContentOpenActionsService
   */

  getActionsForContent(
    content: ContentInterface,
    result?: ResultInterface | Result,
    options: { forReview?: boolean; forDWB?: boolean } = { forReview: false, forDWB: false }
  ): ContentActionInterface[] {
    if (!(result instanceof Result)) result = Result.toResult(result);

    return this.getContentActions(
      content,
      options.forReview,
      (result?.eduContent?.questions ?? result?.eduContent?.publishedEduContentMetadata?.questions ?? []).some(
        (q) => q.needsGrading
      ) ?? content?.questions?.some((q) => q.needsGrading)
    ).map((action) => ({ ...action, disabled: !result?.isAttempted }));
  }

  getActionForContent(content: ContentInterface, actionType: ContentActionType): ContentActionInterface {
    const actions = this.getContentActions(content);
    const action = actions.find((a) => a === this.contentActionDictionary[actionType]);
    return action;
  }

  getActionsForTaskInstanceEduContent(
    eduContent: EduContent,
    result: Pick<ResultInterface, 'status'>,
    taskInstance: Partial<StudentTaskWithContentInterface>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface
  ): ContentActionInterface[] {
    throw new Error('Method not implemented.');
  }

  private getContentActions(
    content: ContentInterface,
    forReview?: boolean,
    hasGradingQuestions?: boolean
  ): ContentActionInterface[] {
    if (content instanceof EduContent && (content as EduContent).isExercise) {
      return this.getExerciseContentActions({
        forReview,
        withPreview: !content.isGeogebra,
        previewOnly: content.type !== EduContentTypeEnum.EXERCISE, // is SCORM but not LUDO
        useGrading: hasGradingQuestions,
      });
    }

    switch (content.type) {
      case EduContentTypeEnum.BOEKE:
        return [this.contentActionDictionary.openBoeke];
      case EduContentTypeEnum.SPEECH:
        return [this.contentActionDictionary.openEduContentAsDownload];
      case EduContentTypeEnum.PAPER_EXERCISE:
        return [
          this.contentActionDictionary.openPaperExercise,
          this.contentActionDictionary.openPaperExerciseAsSolution,
        ];

      case EduContentTypeEnum.WHITEBOARD:
      case EduContentTypeEnum.TIMELINE:
      case EduContentTypeEnum.LINK:
        return [this.contentActionDictionary.openEduContentAsLink];
      case EduContentTypeEnum.WEB_APP:
        if (content instanceof EduContent && content.hasInstructions) {
          return [this.contentActionDictionary.openEduContentAsWebApp, this.contentActionDictionary.openInstructions];
        }

        return [this.contentActionDictionary.openEduContentAsWebApp];
      case EduContentTypeEnum.TEXT:
        return [this.contentActionDictionary.openEduContentAsHtml];
    }

    if (content instanceof EduContent && content.streamable) {
      return [this.contentActionDictionary.openEduContentAsStream];
    } else {
      const eduFileActions = this.getEduFileActions(content);
      const linkActions = this.getLinkActions(content);
      return [...eduFileActions, ...linkActions];
    }
  }

  private getEduFileActions(content: ContentInterface): ContentActionInterface[] {
    if (!(content instanceof EduContent)) return [];

    const actions = content.files.map((file, index) => {
      const isPrimary = index === 0;

      if (isPrimary) {
        return {
          ...this.contentActionDictionary.openEduContentAsDownload,
          ...(file.id && { options: { eduFileId: file.id } }),
        };
      }
      const fileName = file.fileName ?? '';
      return {
        label: `Download als ${fileName.split('.').pop()}`,
        icon: 'download',
        tooltip: `Download ${fileName}`,
        handler: this.getHandler('openEduFileAsDownload'),
        options: {
          eduFileId: file.id,
        },
        isPrimary: false,
      };
    });

    return actions;
  }

  private getLinkActions(content: ContentInterface): ContentActionInterface[] {
    if (!(content instanceof EduContent)) return [];

    return content.links.map((link) => ({
      label: 'Open in Google Docs',
      icon: 'open-in-new',
      tooltip: `Open ${link.url}`,
      handler: this.getHandler('openLink'),
      options: {
        linkId: link.id,
      },
    }));
  }

  private getHandler(name: keyof ContentOpenerInterface) {
    const handler =
      this.contentOpener[name]?.bind(this.contentOpener) ??
      this.defaultContentOpener[name]?.bind(this.defaultContentOpener);
    if (!handler) return undefined;

    return (...args: any[]) => {
      const handlerContext = this.contentActionDictionary[name];

      this.dispatchSideEffectActions(handlerContext, ...(args as any));
      return handler(...args);
    };
  }

  private getExerciseContentActions({
    withPreview,
    previewOnly,
    forReview,
    useGrading,
  }: {
    withPreview: boolean;
    previewOnly?: boolean;
    forReview?: boolean;
    useGrading?: boolean;
  }) {
    // forReview has priority over previewOnly
    if (forReview) {
      if (useGrading) {
        return [
          this.contentActionDictionary.openEduContentForReview,
          this.contentActionDictionary.openEduContentForGrading,
        ];
      }

      return [this.contentActionDictionary.openEduContentForReview];
    }

    if (previewOnly) {
      return [this.contentActionDictionary.openEduContentAsPreview];
    }

    const result = [this.contentActionDictionary.openEduContentAsExercise];

    if (withPreview) {
      result.push(this.contentActionDictionary.openEduContentAsSolution);
    }

    return result;
  }

  private dispatchSideEffectActions<K extends keyof ContentOpenerInterface>(
    handlerContext: ContentActionInterface,
    ...actionArgs: Parameters<ContentOpenerInterface[K]>
  ) {
    const eduContent = actionArgs[0];
    if (!eduContent) return;

    const eduContentHasMinimal = 'minimal' in eduContent;

    this.store.dispatch(
      EventTrackingActions?.trackEduContentInteraction?.({
        action: 'edu_content_opened',
        elementId: 'edu-content-interaction',
        properties: {
          eduContent: eduContentHasMinimal ? eduContent.minimal : eduContent,
          handler: handlerContext,
        },
      })
    );
  }
}
